import React, {KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Dialog,
    Divider,
    InputAdornment,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    TextField,
    Typography,
} from '@mui/material';
import {GroupedVirtuoso, GroupedVirtuosoHandle} from 'react-virtuoso';
import SearchIcon from '@mui/icons-material/Search';
import {GoToCategory} from './GoToItem';
import {useNavigate} from "react-router-dom";
import {usePageUrl} from "../../app-layout/AppPages";
import {useSidebarGoToItems} from "./providers/useSidebarGoToItems";
import {useEmployeeGoToItems} from "./providers/useEmployeeGoToItems";
import {useNeighbourhoodGoToItems} from "./providers/useNeighbourhoodGoToItems";
import {useKeyDataGoToItems} from "./providers/useKeyDataGoToItems";
import {RemoteValue} from "../../util/RemoteValue";
import {Loading} from "../loading-data/LoadingData";
import {useOfficeGoToItems} from "./providers/useOfficeGoToItems";

type GoToItemIndex = { categoryIndex: number, itemIndex: number };
const zeroIndex = {categoryIndex: 0, itemIndex: 0};
function dataIndex(index: GoToItemIndex) {
    return `${index.categoryIndex}-${index.itemIndex}`;
}

function incIndex(categories: GoToCategory[], index: GoToItemIndex): GoToItemIndex {
    return index.categoryIndex < categories.length - 1 ? (
        index.itemIndex < categories[index.categoryIndex].items.length - 1 ? {
            categoryIndex: index.categoryIndex,
            itemIndex: index.itemIndex + 1,
        } : {
            categoryIndex: index.categoryIndex + 1,
            itemIndex: 0,
        }
     ) : index.categoryIndex === categories.length - 1 ? (
        index.itemIndex < categories[index.categoryIndex].items.length - 1 ? {
            categoryIndex: index.categoryIndex,
            itemIndex: index.itemIndex + 1,
        } : {
            categoryIndex: index.categoryIndex,
            itemIndex: categories[index.categoryIndex].items.length - 1,
        }
    ) : {
        categoryIndex: categories.length - 1,
        itemIndex: categories[categories.length - 1].items.length - 1,
    }
}

function decIndex(categories: GoToCategory[], index: GoToItemIndex): GoToItemIndex {
    return index.categoryIndex > 0 ? (
        index.itemIndex > 0 ? {
            categoryIndex: index.categoryIndex,
            itemIndex: index.itemIndex - 1,
        } : {
            categoryIndex: index.categoryIndex - 1,
            itemIndex: categories[index.categoryIndex - 1].items.length - 1,
        }
    ) : index.categoryIndex === 0 ? (
        index.itemIndex > 0 ? {
            categoryIndex: index.categoryIndex,
            itemIndex: index.itemIndex - 1,
        } : {
            categoryIndex: 0,
            itemIndex: 0,
        }
    ) : {
        categoryIndex: 0,
        itemIndex: 0,
    }
}

function eqIndex(a: GoToItemIndex, b: GoToItemIndex) {
    return a.categoryIndex === b.categoryIndex && a.itemIndex === b.itemIndex;
}

function flatIndex(categories: GoToCategory[], index: GoToItemIndex) {
    return categories
        .slice(0, index.categoryIndex)
        .reduce((count, category) => count + category.items.length, index.itemIndex);
}

function GoToPopupContent({open, close, categories}: {
    open: boolean;
    close: () => void;
    categories: GoToCategory[];
}) {
    const [query, setQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState<GoToItemIndex>(zeroIndex);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<GroupedVirtuosoHandle>(null);
    const navigate = useNavigate();
    const pageUrl = usePageUrl();

    const filteredCategories = useMemo(
        () => {
            const q = query.trim().toLowerCase();

            return categories
                .map(category => ({
                    name: category.name,
                    items: (
                        q
                        ? category.items.filter(item =>
                            item.label.toLowerCase().includes(q) ||
                            item.sublabel?.toLowerCase().includes(q)
                        )
                        : category.items
                    ),
                }))
                .filter(category => category.items.length > 0)
                .map((category, categoryIndex) => ({
                    name: category.name,
                    items: category.items.map((item, itemIndex) => ({
                        ...item,
                        index: {categoryIndex, itemIndex},
                    }))
                }));
        },
        [categories, query],
    );
    const listHeight = useMemo(
        () => filteredCategories.reduce(
            (height, category) => height + 36 + category.items.reduce(
                (categoryHeight, item) => categoryHeight + (item.sublabel ? 64 : 48),
                0,
            ),
            8,
        ),
        [filteredCategories],
    );

    useEffect(() => {
        setFocusedIndex(zeroIndex);
    }, [query]);

    useEffect(() => {
        if (open) {
            setQuery('');
            setFocusedIndex(zeroIndex);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        listRef.current?.scrollIntoView({
            index: flatIndex(filteredCategories, focusedIndex),
            behavior: 'auto',
            done: () => undefined,
        });
    }, [filteredCategories, focusedIndex, open]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                setFocusedIndex(prevIndex => incIndex(filteredCategories, prevIndex));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                e.stopPropagation();
                setFocusedIndex(prevIndex => decIndex(filteredCategories, prevIndex));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                if (filteredCategories.length === 0) return;
                const item = filteredCategories[focusedIndex.categoryIndex].items[focusedIndex.itemIndex];
                close();
                navigate(item.url(pageUrl))
            }
        },
        [navigate, pageUrl, setFocusedIndex, filteredCategories, focusedIndex.categoryIndex, focusedIndex.itemIndex, close],
    );

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth="sm"
            slotProps={{
                container: {sx: {alignItems: 'flex-start'}},
                paper: {sx: {overflow: 'hidden'}},
                transition: {onEntered: () => inputRef.current?.focus()}
            }}
        >
            <TextField
                inputRef={inputRef}
                fullWidth
                placeholder="Go to..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                        disableUnderline: true,
                        sx: {fontSize: '1.1rem'},
                    },
                }}
                variant="standard"
                sx={{px: 2, pt: 1.5, pb: 1}}
            />
            <Divider/>
            {filteredCategories.length === 0 ? (
                <Typography color="text.secondary" sx={{px: 2, py: 3, textAlign: 'center'}}>
                    No matching results found
                </Typography>
            ) : (
                <GroupedVirtuoso
                    ref={listRef}
                    groupCounts={filteredCategories.map(category => category.items.length)}
                    style={{height: listHeight, maxHeight: '60vh'}}
                    groupContent={categoryIndex => {
                        const category = filteredCategories[categoryIndex];
                        return category ? (
                            <ListSubheader sx={{lineHeight: '32px', mt: 0.5}}>
                                {category.name}
                            </ListSubheader>
                        ) : null;
                    }}
                    itemContent={(itemIndex, categoryIndex) => {
                        const category = filteredCategories[categoryIndex];
                        if (!category) return null;
                        const categoryItemIndex = itemIndex - flatIndex(
                            filteredCategories,
                            {categoryIndex, itemIndex: 0},
                        );
                        const item = category.items[categoryItemIndex];
                        if (!item) return null;
                        return (
                            <ListItemButton
                                dense
                                data-index={dataIndex(item.index)}
                                selected={eqIndex(item.index, focusedIndex)}
                                onClick={() => {
                                    close();
                                    navigate(item.url(pageUrl));
                                }}
                                onMouseEnter={() => setFocusedIndex(item.index)}
                                sx={{px: 3}}
                            >
                                {item.icon && (
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        {item.icon()}
                                    </ListItemIcon>
                                )}
                                <ListItemText primary={item.label} secondary={item.sublabel}/>
                            </ListItemButton>
                        );
                    }}
                />
            )}
        </Dialog>
    );
}

export function GoToPopup({open, close}: {
    open: boolean;
    close: () => void;
}) {
    const sidebarItems = useSidebarGoToItems();
    const employeeItems = useEmployeeGoToItems();
    const neighbourhoodItems = useNeighbourhoodGoToItems();
    const keyDataItems = useKeyDataGoToItems();
    const officeItems = useOfficeGoToItems();

    const categories = useMemo(
        () => RemoteValue
            .combine({sidebarItems, officeItems, employeeItems, neighbourhoodItems, keyDataItems})
            .map(({sidebarItems, officeItems, employeeItems, neighbourhoodItems, keyDataItems}) =>
                [sidebarItems, officeItems, employeeItems, neighbourhoodItems, keyDataItems]
            ),
        [sidebarItems, officeItems, employeeItems, neighbourhoodItems, keyDataItems],
    );

    return (
        <Loading value={categories}>{ categories =>
            <GoToPopupContent
                open={open}
                close={close}
                categories={categories}
            />
        }</Loading>
    );
}
